import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Palette, RefreshCw, AlertCircle, CheckCircle2, Loader2, Edit3,
  X as XIcon, Save as SaveIcon, Upload, Trash2, FileText, Image as ImageIcon,
  Type as TypeIcon, FileBadge, Hash, Users, Megaphone, Calendar as CalendarIcon,
  Shield, Quote, Sparkles,
} from "lucide-react";
import { SocialTemplatesTab } from "./SocialTemplatesTab.js";
import { ArticleSlidesTab } from "./ArticleSlidesTab.js";
import { api, type BrandFileEntry, type BrandManifest, type BrandSectionEntry, type BrandAssetEntry } from "../../lib/api.js";

/**
 * Brand kit — in-app editor for the canonical brand/ folder on disk.
 *
 * Layout:
 *   - Header with manifest summary + "Reload cache" button
 *   - Asset library card (upload, preview, delete)
 *   - 8 section cards (voice / identity / audience / credentialing /
 *     disclaimers / sources / distribution / calendar). Each card surfaces
 *     section-specific typed widgets where they exist (color swatches,
 *     persona cards, hashtag chips) PLUS a raw textarea editor accessible via
 *     the "Edit" button on every file.
 *   - Top-level files (README.md) at the bottom.
 *
 * Edits hit `PUT /api/brand/files` which validates JSON shape before write
 * and calls `resetBrandCache()` on success — the next pipeline action sees the
 * new content with no ui-server restart.
 */
type BrandTab = "kit" | "templates" | "slides";

/**
 * The tab lives in the URL hash, not in component state.
 *
 * The operator's complaint was that the templates link "disappears on refresh".
 * With state-only tabs it would: a reload drops you back on the brand files and
 * the gallery becomes unreachable except by clicking. `#/brand?tab=templates` is
 * bookmarkable and survives the dev server restarting.
 */
function initialTab(): BrandTab {
  const q = window.location.hash.split("?")[1] ?? "";
  return new URLSearchParams(q).get("tab") === "templates" ? "templates" : "kit";
}

export function BrandPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<BrandFileEntry | null>(null);
  const [tab, setTab] = useState<BrandTab>(initialTab);

  const pickTab = (t: BrandTab) => {
    setTab(t);
    const base = window.location.hash.split("?")[0] || "#/brand";
    window.history.replaceState(null, "", t === "templates" ? `${base}?tab=templates` : base);
  };

  const manifestQuery = useQuery({
    queryKey: ["brand", "manifest"],
    queryFn: () => api.getBrandManifest(),
    refetchOnWindowFocus: true,
  });

  const reloadMutation = useMutation({
    mutationFn: () => api.reloadBrand(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brand"] }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["brand"] });

  const manifest = manifestQuery.data;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-brand-700" strokeWidth={2.2} />
            <h1 className="text-2xl font-semibold">Brand kit</h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            {tab === "templates" ? (
              <>Social post templates for therapist spotlights. Live preview per provider, per format, per background — each card is the exact document the PNG exporter renders.</>
            ) : (
              <>Edit AlternaBe's voice guidelines, color palette, audience personas, hashtags, disclaimers, citation sources, and brand assets. Changes write to <code className="rounded bg-line px-1 py-0.5 font-mono text-xs">brand/</code> on disk and reload the in-memory cache immediately — no ui-server restart needed.</>
            )}
          </p>
        </div>
        <button
          onClick={() => reloadMutation.mutate()}
          disabled={reloadMutation.isPending}
          hidden={tab !== "kit"}
          className="btn-ghost shrink-0 text-sm"
          title="Flush the brand loader cache. Useful if you edited a brand/ file directly on disk."
        >
          {reloadMutation.isPending ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Reloading…</>
          ) : (
            <><RefreshCw className="h-4 w-4" /> Reload cache</>
          )}
        </button>
      </header>

      {tab === "kit" && reloadMutation.data?.ok && (
        <div className="rounded-lg border border-accent/30 bg-accent-soft px-4 py-2 text-xs">
          <div className="flex items-center gap-2 font-medium text-emerald-900">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Cache reloaded — orchestrators now see the latest brand content.
          </div>
        </div>
      )}

      <div className="flex gap-1 border-b border-line">
        {([["kit", "Brand kit"], ["templates", "Social templates"], ["slides", "Article slides"]] as [BrandTab, string][]).map(([k, label]) => (
          <button
            key={k}
            onClick={() => pickTab(k)}
            className={`-mb-px border-b-2 px-3.5 py-2 text-sm ${tab === k ? "border-brand-600 font-medium text-brand-800" : "border-transparent text-muted hover:text-ink"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "templates" && <SocialTemplatesTab />}
      {tab === "slides" && <ArticleSlidesTab />}

      {tab === "kit" && manifestQuery.isPending && (
        <div className="card p-6 text-sm text-muted">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading brand manifest…
        </div>
      )}

      {tab === "kit" && manifestQuery.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-xs text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Manifest failed: {(manifestQuery.error as Error).message}</span>
        </div>
      )}

      {tab === "kit" && manifest && !manifest.available && (
        <div className="card p-6 text-sm text-muted">
          The <code className="rounded bg-line px-1 py-0.5 font-mono text-xs">brand/</code> folder is missing on disk. The pipeline still runs using in-code defaults, but nothing is editable until you create it (see <code className="font-mono text-xs">brand/README.md</code> in the repo).
        </div>
      )}

      {tab === "kit" && manifest?.available && (
        <>
          <BrandManifestSummary manifest={manifest} />
          <AssetsCard manifest={manifest} onChanged={invalidate} />
          {manifest.sections.map((section) => (
            <SectionCard
              key={section.name}
              section={section}
              onEdit={(file) => setEditing(file)}
            />
          ))}
          {manifest.topLevelFiles.length > 0 && (
            <SectionCard
              section={{ name: "(top-level)", files: manifest.topLevelFiles }}
              onEdit={(file) => setEditing(file)}
            />
          )}
        </>
      )}

      {editing && (
        <RawEditorModal
          file={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}
    </div>
  );
}

// ── Manifest summary ──────────────────────────────────────────────────────────

function BrandManifestSummary({ manifest }: { manifest: BrandManifest }) {
  const fileCount = manifest.sections.reduce((acc, s) => acc + s.files.length, 0) + manifest.topLevelFiles.length;
  const totalBytes = [
    ...manifest.sections.flatMap((s) => s.files),
    ...manifest.topLevelFiles,
  ].reduce((acc, f) => acc + f.sizeBytes, 0);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiTile label="Sections" value={manifest.sections.filter((s) => s.files.length > 0).length} />
      <KpiTile label="Files" value={fileCount} />
      <KpiTile label="Assets" value={manifest.assets.length} />
      <KpiTile label="Total size" value={`${(totalBytes / 1024).toFixed(1)} KB`} />
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: number | string | undefined }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-2xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-brand">{value ?? "—"}</div>
    </div>
  );
}

// ── Section card ─────────────────────────────────────────────────────────────

const SECTION_ICONS: Record<string, typeof FileText> = {
  voice: Quote,
  identity: Sparkles,
  audience: Users,
  credentialing: Shield,
  disclaimers: FileBadge,
  sources: FileText,
  distribution: Megaphone,
  calendar: CalendarIcon,
};

const SECTION_DESCRIPTIONS: Record<string, string> = {
  voice: "LLM voice block, canonical spellings, glossary, tone matrix. Injected into every production prompt.",
  identity: "Color palette (also feeds Tailwind + Remotion), typography, image-gen style guide (auto-prepended to every Flux prompt).",
  audience: "Buyer personas. Storyboard picks the right persona per article based on cluster.",
  credentialing: "Therapist standards by modality (e.g. \"200 training hours for reflexology\"). Articles cite real numbers.",
  disclaimers: "HE + EN health disclaimers appended to every produced article footer.",
  sources: "Trusted citation domains + never-cite categories. Constrains the brief generator's sources[].",
  distribution: "Per-channel hashtags, CTAs, platform bios.",
  calendar: "Israeli content dates (holidays + seasonal). Discovery sweeper boosts cluster-aligned topics around these.",
};

function SectionCard({ section, onEdit }: { section: BrandSectionEntry; onEdit: (f: BrandFileEntry) => void }) {
  if (section.files.length === 0) return null;
  const Icon = SECTION_ICONS[section.name] ?? FileText;
  const description = SECTION_DESCRIPTIONS[section.name];
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-line bg-canvas-soft px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-brand-700" strokeWidth={2.2} />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-ink">{section.name}</h2>
        </div>
        {description && <p className="mt-0.5 text-xs text-muted">{description}</p>}
      </div>
      <div className="divide-y divide-line">
        {section.files.map((file) => (
          <div key={file.path} className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium text-ink">
                  <code className="font-mono text-xs text-brand-700">{file.path}</code>
                  <FileTypeBadge kind={file.kind} />
                  <span className="text-2xs text-muted">{(file.sizeBytes / 1024).toFixed(1)} KB</span>
                </div>
                {file.jsonParseError && (
                  <div className="mt-1 flex items-start gap-1 text-2xs text-danger">
                    <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>JSON parse error: {file.jsonParseError}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => onEdit(file)}
                className="btn-ghost shrink-0 text-xs"
                title="Open the raw text editor"
              >
                <Edit3 className="h-3 w-3" />
                Edit
              </button>
            </div>
            <div className="mt-2">
              <FileWidget file={file} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FileTypeBadge({ kind }: { kind: BrandFileEntry["kind"] }) {
  const color =
    kind === "md" ? "bg-sky-100 text-sky-700" :
    kind === "json" ? "bg-violet-100 text-violet-700" :
    kind === "yaml" ? "bg-amber-100 text-amber-700" :
    "bg-line text-muted-dark";
  return <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${color}`}>{kind.toUpperCase()}</span>;
}

// ── Per-file widgets (typed where it pays off, preview otherwise) ────────────

function FileWidget({ file }: { file: BrandFileEntry }) {
  // Section-specific typed widgets ride on top of the raw content cache so
  // the operator gets a real preview without clicking Edit.
  if (file.path === "identity/colors.json" && file.parsedJson) {
    return <ColorPaletteWidget data={file.parsedJson as BrandColorsShape} />;
  }
  if (file.path === "audience/personas.json" && file.parsedJson) {
    return <PersonasWidget data={file.parsedJson as PersonasShape} />;
  }
  if (file.path === "distribution/hashtags.json" && file.parsedJson) {
    return <HashtagsWidget data={file.parsedJson as HashtagsShape} />;
  }
  if (file.path === "voice/canonical-names.json" && file.parsedJson) {
    return <CanonicalNamesWidget data={file.parsedJson as CanonicalNamesShape} />;
  }
  if (file.path === "sources/trusted-citations.json" && file.parsedJson) {
    return <TrustedSourcesWidget data={file.parsedJson as TrustedShape} />;
  }
  // Markdown preview — first 6 non-blank lines
  if (file.kind === "md") {
    const preview = file.content
      .replace(/^---\n[\s\S]*?\n---\n/, "")
      .split("\n")
      .filter((l) => l.trim().length > 0)
      .slice(0, 6)
      .join("\n");
    return (
      <pre className="max-h-48 overflow-auto rounded-md border border-line bg-canvas px-3 py-2 text-xs text-muted-dark whitespace-pre-wrap" dir="auto">
        {preview}
        {file.content.split("\n").filter((l) => l.trim().length > 0).length > 6 && "\n…"}
      </pre>
    );
  }
  // Generic JSON preview
  if (file.kind === "json") {
    return (
      <pre className="max-h-48 overflow-auto rounded-md border border-line bg-canvas px-3 py-2 font-mono text-[11px] text-muted-dark">
        {JSON.stringify(file.parsedJson, null, 2).slice(0, 600)}
        {JSON.stringify(file.parsedJson, null, 2).length > 600 && "\n…"}
      </pre>
    );
  }
  return null;
}

// ── Color palette widget ─────────────────────────────────────────────────────

interface BrandColorsShape {
  primary?: { brand?: { DEFAULT?: string; scale?: Record<string, string>; _role?: string } };
  neutrals?: Record<string, { DEFAULT?: string; _role?: string } & Record<string, string>>;
  semantic?: Record<string, { DEFAULT?: string; _role?: string } & Record<string, string>>;
}

function ColorPaletteWidget({ data }: { data: BrandColorsShape }) {
  const swatches: { group: string; entries: { name: string; hex: string; role?: string }[] }[] = [];
  if (data.primary?.brand) {
    const b = data.primary.brand;
    const entries: { name: string; hex: string; role?: string }[] = [];
    if (b.DEFAULT) entries.push({ name: "brand", hex: b.DEFAULT, role: b._role });
    if (b.scale) {
      for (const [k, v] of Object.entries(b.scale)) entries.push({ name: `brand-${k}`, hex: v });
    }
    swatches.push({ group: "Primary", entries });
  }
  for (const [groupKey, sourceGroup] of [["Neutrals", data.neutrals], ["Semantic", data.semantic]] as const) {
    if (!sourceGroup) continue;
    const entries: { name: string; hex: string; role?: string }[] = [];
    for (const [name, def] of Object.entries(sourceGroup)) {
      for (const [variant, hex] of Object.entries(def)) {
        if (variant.startsWith("_") || typeof hex !== "string") continue;
        entries.push({ name: variant === "DEFAULT" ? name : `${name}-${variant}`, hex, role: def._role });
      }
    }
    if (entries.length > 0) swatches.push({ group: groupKey, entries });
  }
  return (
    <div className="space-y-2">
      {swatches.map((g) => (
        <div key={g.group}>
          <div className="text-2xs uppercase tracking-wider text-muted">{g.group}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {g.entries.map((sw) => (
              <div key={sw.name} className="flex items-center gap-1.5 rounded-md border border-line bg-white px-2 py-1" title={sw.role ?? sw.name}>
                <span className="h-4 w-4 shrink-0 rounded border border-line/70" style={{ background: sw.hex }} />
                <span className="font-mono text-[11px] text-muted-dark">{sw.name}</span>
                <span className="font-mono text-[10px] text-muted/70">{sw.hex}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Personas widget ──────────────────────────────────────────────────────────

interface PersonasShape {
  default?: string;
  personas?: { id: string; displayName: string; ageRange?: string; location?: string; context?: string; addressAs?: string; avoid?: string; clusters?: string[] }[];
}

function PersonasWidget({ data }: { data: PersonasShape }) {
  if (!data.personas || data.personas.length === 0) return <div className="text-xs italic text-muted">No personas defined.</div>;
  return (
    <div className="grid gap-2 md:grid-cols-2">
      {data.personas.map((p) => (
        <div key={p.id} className="rounded-md border border-line bg-white p-2.5">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm text-ink">{p.displayName}</span>
            {data.default === p.id && (
              <span className="rounded bg-accent-soft px-1 py-0.5 text-[10px] text-emerald-700">default</span>
            )}
          </div>
          {(p.ageRange || p.location) && (
            <div className="mt-0.5 text-2xs text-muted">{[p.ageRange, p.location].filter(Boolean).join(" · ")}</div>
          )}
          {p.context && <div className="mt-1 line-clamp-2 text-xs text-muted-dark" dir="auto">{p.context}</div>}
          {p.clusters && p.clusters.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {p.clusters.map((c) => (
                <span key={c} className="rounded-full bg-line-soft px-1.5 py-0.5 text-[10px] text-muted-dark">{c}</span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Hashtags widget ──────────────────────────────────────────────────────────

interface HashtagsShape {
  brand?: { primary?: string[] };
  perChannel?: Record<string, { maxHashtags?: number; globalAddOns?: string[] }>;
  byCluster?: Record<string, { he?: string[]; en?: string[] }>;
  neverUse?: { list?: string[] };
}

function HashtagsWidget({ data }: { data: HashtagsShape }) {
  return (
    <div className="space-y-2 text-xs">
      {data.brand?.primary && (
        <Chips label="Brand primary" items={data.brand.primary} />
      )}
      {data.perChannel && Object.keys(data.perChannel).length > 0 && (
        <div>
          <div className="text-2xs uppercase tracking-wider text-muted">Per-channel limits</div>
          <div className="mt-1 grid gap-1 md:grid-cols-2">
            {Object.entries(data.perChannel).map(([ch, cfg]) => (
              <div key={ch} className="rounded-md border border-line bg-white px-2 py-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-ink">{ch}</span>
                  <span className="text-2xs text-muted">max {cfg.maxHashtags ?? "—"}</span>
                </div>
                {cfg.globalAddOns && cfg.globalAddOns.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-0.5">
                    {cfg.globalAddOns.map((t) => <Tag key={t}>{t}</Tag>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {data.byCluster && Object.keys(data.byCluster).length > 0 && (
        <div>
          <div className="text-2xs uppercase tracking-wider text-muted">Per-cluster pools</div>
          <div className="mt-1 space-y-0.5">
            {Object.entries(data.byCluster).slice(0, 4).map(([cluster, pools]) => (
              <div key={cluster} className="text-2xs">
                <span className="font-medium text-ink">{cluster}:</span>{" "}
                {(pools.he ?? []).slice(0, 5).map((t) => <Tag key={t}>{t}</Tag>)}
                {(pools.en ?? []).slice(0, 5).map((t) => <Tag key={t}>{t}</Tag>)}
              </div>
            ))}
            {Object.keys(data.byCluster).length > 4 && (
              <div className="text-2xs text-muted">…{Object.keys(data.byCluster).length - 4} more clusters</div>
            )}
          </div>
        </div>
      )}
      {data.neverUse?.list && data.neverUse.list.length > 0 && (
        <Chips label="Never use" items={data.neverUse.list} danger />
      )}
    </div>
  );
}

function Chips({ label, items, danger }: { label: string; items: string[]; danger?: boolean }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-1 flex flex-wrap gap-1">
        {items.map((t) => (
          <span key={t} className={`rounded-full px-1.5 py-0.5 text-[10px] ${danger ? "bg-danger-soft text-danger" : "bg-brand-50 text-brand-800"}`}>
            #{t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return <span className="mr-0.5 inline-block rounded-full bg-line-soft px-1.5 py-0.5 text-[10px] text-muted-dark">#{children}</span>;
}

// ── Canonical names widget ───────────────────────────────────────────────────

interface CanonicalNamesShape {
  canonical?: { he?: string; en?: string };
  heVariantsLongestFirst?: string[];
  enVariants?: string[];
}

function CanonicalNamesWidget({ data }: { data: CanonicalNamesShape }) {
  return (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-2xs uppercase tracking-wider text-muted">Canonical:</span>
        <code className="rounded bg-brand-50 px-1.5 py-0.5 font-mono text-xs text-brand-800">{data.canonical?.en ?? "—"}</code>
        <code className="rounded bg-brand-50 px-1.5 py-0.5 font-mono text-xs text-brand-800" dir="rtl">{data.canonical?.he ?? "—"}</code>
      </div>
      {data.heVariantsLongestFirst && data.heVariantsLongestFirst.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-2xs uppercase tracking-wider text-muted">HE variants caught:</span>
          {data.heVariantsLongestFirst.slice(0, 6).map((v) => (
            <span key={v} className="rounded-full bg-warn/10 px-1.5 py-0.5 font-mono text-[10px] text-warn" dir="rtl">{v}</span>
          ))}
          {data.heVariantsLongestFirst.length > 6 && (
            <span className="text-2xs text-muted">+{data.heVariantsLongestFirst.length - 6}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Trusted sources widget ───────────────────────────────────────────────────

interface TrustedShape {
  trustedPublishers?: { name: string; domain: string; kind?: string; weight?: number }[];
}

function TrustedSourcesWidget({ data }: { data: TrustedShape }) {
  const items = data.trustedPublishers ?? [];
  if (items.length === 0) return <div className="text-xs italic text-muted">No trusted publishers defined.</div>;
  return (
    <table className="w-full text-xs">
      <thead className="text-2xs uppercase tracking-wider text-muted">
        <tr>
          <th className="pb-1 text-left font-normal">Name</th>
          <th className="pb-1 text-left font-normal">Domain</th>
          <th className="pb-1 text-left font-normal">Kind</th>
          <th className="pb-1 text-right font-normal">Weight</th>
        </tr>
      </thead>
      <tbody>
        {items.slice(0, 8).map((p) => (
          <tr key={p.domain} className="border-t border-line/50">
            <td className="py-0.5 pr-2 font-medium text-ink">{p.name}</td>
            <td className="py-0.5 pr-2 font-mono text-[11px] text-muted-dark">{p.domain}</td>
            <td className="py-0.5 pr-2 text-muted">{p.kind ?? "—"}</td>
            <td className="py-0.5 text-right font-mono tabular-nums">{p.weight ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Asset card ───────────────────────────────────────────────────────────────

function AssetsCard({ manifest, onChanged }: { manifest: BrandManifest; onChanged: () => void }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    setUploadError(null);
    try {
      const base64 = await fileToBase64(file);
      const res = await api.uploadBrandAsset({ name: file.name, contentBase64: base64 });
      if (!res.ok) throw new Error(res.error ?? "upload failed");
      onChanged();
    } catch (e) {
      setUploadError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void upload(f);
    e.target.value = "";
  };

  return (
    <div className="card overflow-hidden">
      <div className="border-b border-line bg-canvas-soft px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-brand-700" strokeWidth={2.2} />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-ink">Brand assets</h2>
            </div>
            <p className="mt-0.5 text-xs text-muted">
              Logos, fonts, brand reference images. Stored at <code className="rounded bg-line px-1 py-0.5 font-mono text-[11px]">brand/assets/</code>. Accepted: PNG/JPG/WebP/GIF/SVG, WOFF/WOFF2/TTF/OTF, PDF. Max 8 MB each.
            </p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            className="btn-primary shrink-0 text-xs"
          >
            {busy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…</>) : (<><Upload className="h-3.5 w-3.5" /> Upload asset</>)}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,font/woff,font/woff2,font/ttf,font/otf,application/pdf,.png,.jpg,.jpeg,.webp,.gif,.svg,.woff,.woff2,.ttf,.otf,.pdf"
            onChange={onPick}
            className="hidden"
          />
        </div>
        {uploadError && (
          <div className="mt-1.5 flex items-start gap-1 text-2xs text-danger">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            <span>{uploadError}</span>
          </div>
        )}
      </div>
      <div className="p-4">
        {manifest.assets.length === 0 ? (
          <div className="text-xs italic text-muted">
            No assets uploaded yet. Drop a logo or brand-reference image to get started.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {manifest.assets.map((a) => (
              <AssetTile key={a.name} asset={a} onDeleted={onChanged} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AssetTile({ asset, onDeleted }: { asset: BrandAssetEntry; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteBrandAsset(asset.name),
    onSuccess: onDeleted,
  });
  return (
    <div className="group relative overflow-hidden rounded-md border border-line bg-white">
      <div className="aspect-square bg-canvas">
        {asset.kind === "image" ? (
          <img src={asset.url} alt={asset.name} className="h-full w-full object-contain" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-muted">
            {asset.kind === "font" ? <TypeIcon className="h-8 w-8" /> : <FileText className="h-8 w-8" />}
            <span className="font-mono text-[10px] uppercase">{asset.ext.replace(".", "")}</span>
          </div>
        )}
      </div>
      <div className="border-t border-line px-2 py-1">
        <div className="truncate font-mono text-[11px] text-ink" title={asset.name}>{asset.name}</div>
        <div className="text-[10px] text-muted">{(asset.sizeBytes / 1024).toFixed(1)} KB</div>
      </div>
      <div className="absolute right-1 top-1 opacity-0 transition group-hover:opacity-100">
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            className="rounded bg-white/90 p-1 text-muted shadow-sm hover:text-danger"
            title="Delete asset"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        ) : (
          <div className="flex items-center gap-1 rounded bg-white/95 px-1.5 py-1 text-[10px] shadow-sm">
            <button onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending} className="font-medium text-danger">
              {deleteMutation.isPending ? "…" : "Confirm"}
            </button>
            <button onClick={() => setConfirming(false)} className="text-muted">cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Raw editor modal ─────────────────────────────────────────────────────────

function RawEditorModal({ file, onClose, onSaved }: { file: BrandFileEntry; onClose: () => void; onSaved: () => void }) {
  const [content, setContent] = useState(file.content);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const jsonPreview = useMemo(() => {
    if (file.kind !== "json") return null;
    try {
      JSON.parse(content);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  }, [content, file.kind]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.putBrandFile({ path: file.path, content });
      if (!res.ok) throw new Error(res.error ?? "save failed");
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const dirty = content !== file.content;
  const isRtlMarkdown = file.kind === "md" && /[֐-׿]/.test(content);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-brand-700" />
              <code className="font-mono text-sm text-ink">{file.path}</code>
              <FileTypeBadge kind={file.kind} />
            </div>
            <div className="text-2xs text-muted">{(file.sizeBytes / 1024).toFixed(1)} KB · raw editor</div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-line hover:text-ink"><XIcon className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="block min-h-[400px] w-full resize-y rounded-md border border-line bg-canvas p-3 font-mono text-xs text-ink focus:border-brand focus:outline-none"
            spellCheck={false}
            dir={isRtlMarkdown ? "auto" : "ltr"}
          />
          {jsonPreview && !jsonPreview.ok && (
            <div className="mt-2 flex items-start gap-1.5 rounded-md border border-danger/30 bg-danger-soft p-2 text-xs text-danger">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div>
                <div className="font-medium">JSON parse error — won't save until fixed</div>
                <div className="mt-0.5 font-mono text-[11px]">{jsonPreview.error}</div>
              </div>
            </div>
          )}
          {jsonPreview && jsonPreview.ok && dirty && (
            <div className="mt-2 flex items-center gap-1.5 rounded-md border border-accent/30 bg-accent-soft px-2 py-1 text-xs text-emerald-800">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              JSON looks valid.
            </div>
          )}
          {error && (
            <div className="mt-2 flex items-start gap-1.5 rounded-md border border-danger/30 bg-danger-soft p-2 text-xs text-danger">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-line bg-canvas-soft px-4 py-2.5">
          <div className="text-2xs text-muted">
            Saving writes to disk + reloads the brand cache instantly. <Hash className="inline h-2.5 w-2.5" /> No restart needed.
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={onClose} className="btn-ghost text-xs"><XIcon className="h-3 w-3" /> Cancel</button>
            <button
              onClick={save}
              disabled={busy || !dirty || (jsonPreview ? !jsonPreview.ok : false)}
              className="btn-primary text-xs"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <SaveIcon className="h-3 w-3" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      // result is `data:<mime>;base64,<payload>` — strip the prefix
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("file read failed"));
    reader.readAsDataURL(file);
  });
}
